import { useCallback, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { Button, PillSelect, PillTextField } from "../../components/ui";
import { useThemeColors } from "../../hooks/useThemeColors";
import { InternOption, createAssignment, fetchInterns } from "./jobCardApi";
import { errorMessage, money, tone } from "./jobCardShared";

const PAY_OPTIONS = [
  { value: "flat", label: "Flat reward" },
  { value: "hours", label: "Hours (paid at the intern's rate)" },
];

const CreateAssignmentModal = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) => {
  const colors = useThemeColors();
  const [interns, setInterns] = useState<InternOption[]>([]);
  const [selectedIntern, setSelectedIntern] = useState<InternOption | null>(null);
  const [internSearch, setInternSearch] = useState("");
  const [loadingInterns, setLoadingInterns] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [pay, setPay] = useState("flat");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const internSearchRequest = useRef(0);

  useEffect(() => {
    if (!open) return;
    setSelectedIntern(null);
    setInternSearch("");
    setInterns([]);
    setTitle("");
    setDescription("");
    setDueDate("");
    setPay("flat");
    setAmount("");
    setError(null);
  }, [open]);

  const searchInterns = useCallback(async (search: string) => {
    const requestId = ++internSearchRequest.current;
    setLoadingInterns(true);
    try {
      const results = await fetchInterns(search);
      if (requestId !== internSearchRequest.current) return;
      setInterns(
        !selectedIntern || results.some((intern) => intern.id === selectedIntern.id)
          ? results
          : [selectedIntern, ...results],
      );
      setError(null);
    } catch {
      if (requestId === internSearchRequest.current) {
        setError("Could not load interns. Please try again.");
      }
    } finally {
      if (requestId === internSearchRequest.current) setLoadingInterns(false);
    }
  }, [selectedIntern]);

  useEffect(() => {
    if (!open) return;
    const timeout = window.setTimeout(() => {
      void searchInterns(internSearch);
    }, internSearch.trim() ? 300 : 0);
    return () => window.clearTimeout(timeout);
  }, [internSearch, open, searchInterns]);

  const intern = selectedIntern;
  const amountNum = Number(amount);
  const estimated =
    pay === "hours" && intern?.hourlyRate != null && amount !== "" && !Number.isNaN(amountNum)
      ? amountNum * intern.hourlyRate
      : null;

  const submit = async () => {
    if (!selectedIntern) return setError("Choose an intern.");
    if (!title.trim()) return setError("Enter a title.");
    if (amount === "" || Number.isNaN(amountNum) || amountNum < 0) {
      return setError(pay === "hours" ? "Enter the hours." : "Enter the reward.");
    }
    setBusy(true);
    setError(null);
    try {
      await createAssignment({
        intern: selectedIntern.id,
        title: title.trim(),
        description: description.trim(),
        dueDate,
        reward: pay === "flat" ? amountNum : null,
        hours: pay === "hours" ? amountNum : null,
      });
      onCreated();
      onClose();
    } catch (e) {
      setError(errorMessage(e, "Could not create the assignment."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: colors.primary[900],
          border: `1px solid ${colors.primary[300]}20`,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: colors.primary[100] }}>
        Create assignment
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <Autocomplete
          options={interns}
          value={selectedIntern}
          inputValue={internSearch}
          onChange={(_, value) => {
            setSelectedIntern(value);
            setInternSearch(value?.name ?? "");
            setError(null);
          }}
          onInputChange={(_, value, reason) => {
            if (reason === "input" || reason === "clear") {
              setInternSearch(value);
              if (reason === "input") setSelectedIntern(null);
            }
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options) => options}
          loading={loadingInterns}
          noOptionsText={internSearch.trim() ? "No matching interns" : "No interns available"}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Intern"
              required
              placeholder="Search interns..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingInterns && <CircularProgress size={18} sx={{ mr: 1 }} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  color: colors.primary[100],
                },
                "& .MuiInputLabel-root": { color: colors.primary[300] },
              }}
            />
          )}
        />
        <PillTextField
          label="Title"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <PillTextField
          label="Description"
          multiline
          minRows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <PillTextField
          label="Due date"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <PillSelect
            label="Payment"
            value={pay}
            onChange={(e) => setPay(String(e.target.value))}
            options={PAY_OPTIONS}
          />
          <PillTextField
            label={pay === "hours" ? "Hours" : "Reward ($)"}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: pay === "hours" ? 0.25 : 0.01 }}
          />
        </Box>
        {estimated !== null && (
          <Typography sx={{ fontSize: "0.78rem", color: colors.primary[300] }}>
            About {money(estimated)} at {intern?.name}&apos;s current rate.
          </Typography>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button disabled={busy} onClick={onClose} sx={tone("transparent", colors.primary[100])}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={busy}>
          {busy ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Create assignment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAssignmentModal;
